import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay, take } from 'rxjs';
import {
    CvRequestParams,
    FileType,
} from '../../shared/external-model/cv/cv-request-params.interface';
import { CodeTableService } from '../../core/code-table.service';
import { EntityService } from '../../core/entity.service';
import { JwtUtilService } from '../../core/jwt-util.service';
import { RestApiBase } from '../../shared/configurations/rest-api-base.config';
import { CvTemplateHeaderDTO } from '../../shared/external-model/cv/cv-template-header-dto';
import { CodeLabelEntry } from '../../shared/interfaces/code-label-entry.interface';
import { UrlUtils } from '../../shared/utils/url.utils';
import { CvSectionConfigDTO } from '../../shared/external-model/cv/cv-section-config-dto';
import { CvTemplateDTO } from '../../shared/external-model/cv/cv-template-dto';
import { CvSectionStaticConfig } from 'src/app/shared/external-model/cv/cv-section-static-config.interface';

@Injectable({
    providedIn: 'root',
})
export class CvCreationService extends EntityService {
    private _http = inject(HttpClient);
    private _jus = inject(JwtUtilService);
    private codeTableSevice = inject(CodeTableService);

    primaryFilterMap: { [key: string]: Observable<string[]> };
    sortByList$: Observable<CodeLabelEntry[]>;
    optionsMap: { [key: string]: CvSectionStaticConfig };

    constructor() {
        super();
        this.institutionCode = UrlUtils.getParam('institution');

        this.sortByList$ = this.codeTableSevice
            .getCodeTable('CvConfigSortBy')
            .pipe(shareReplay());

        this.getStaticConfig()
            .pipe(shareReplay())
            .subscribe((config) => {
                this.optionsMap = config.sectionsConfig;
            });
    }

    public getCvTemplates() {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        return this._http.get<{
            otbTemplates: CvTemplateHeaderDTO[];
            userTemplates: CvTemplateHeaderDTO[];
        }>(`${RestApiBase.API_ENDPOINT_CV_CONFIG}/${this._jus.getUserID()}`, {
            params,
        });
    }

    public getCvSections(templateId: string) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );

        return this._http
            .get(
                `${RestApiBase.API_ENDPOINT_CV_CONFIG}/sections/${templateId}`,
                { params },
            )
            .pipe(take(1));
    }

    public getStaticConfig() {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        return this._http.get<any>(
            `${RestApiBase.API_ENDPOINT_CV_CONFIG}/getStaticConfig`,
            { params },
        );
    }

    public generateCvFile(sections: CvSectionConfigDTO[], language: string) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        const requestParams: CvRequestParams = this.getCvRequestParams(
            sections,
            language,
        );
        const _headers = new HttpHeaders();
        return this._http.post(
            `${RestApiBase.API_ENDPOINT_CV_OUTPUT}/generateCv`,
            requestParams,
            {
                params,
                headers: _headers,
                responseType: 'blob',
                observe: 'response',
            },
        );
    }

    public generateCvFileViewer(
        sections: CvSectionConfigDTO[],
        language: string,
    ) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );
        const requestParams: CvRequestParams = this.getCvRequestParams(
            sections,
            language,
        );
        return this._http.post(
            `${RestApiBase.API_ENDPOINT_CV_OUTPUT}/generateCvViewer`,
            requestParams,
            { params },
        );
    }

    public saveTemplate(
        header: CvTemplateHeaderDTO,
        sectionList: CvSectionConfigDTO[],
    ) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );

        const templateDTO: CvTemplateDTO = { header, sectionList };

        return this._http.post(
            `${RestApiBase.API_ENDPOINT_CV_CONFIG}/saveTemplateAs`,
            templateDTO,
            {
                params,
            },
        );
    }
    public deleteTemplate(templateId) {
        const params = new HttpParams().set(
            'institution',
            this.institutionCode,
        );

        return this._http.delete(
            `${RestApiBase.API_ENDPOINT_CV_CONFIG}/${templateId}`,
            {
                params,
            },
        );
    }

    private getCvRequestParams(
        sections: CvSectionConfigDTO[],
        language?: string,
    ): CvRequestParams {
        const params: CvRequestParams = {
            userId: this._jus.getUserID(),
            language,
            sectionList: sections,
            fileType: FileType.rtf,
            fileName: `${this._jus
                .getUserDisplayName()
                ?.split(' ')
                .join('_')}_cv`,
        };
        return params;
    }
}
